// ============================================================
// Tie-In — TriageSettings.jsx
// Full settings screen with key account management,
// emergency phone, company name, and all triage config.
// ============================================================

import { useState, useEffect } from 'react';
import { t } from './triageTheme.js';
import { getConfig, updateConfig } from './triageApi.js';

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: '11px', fontWeight: 600, fontFamily: t.fonts.mono,
      color: t.colors.textMuted, letterSpacing: '0.08em',
      textTransform: 'uppercase', padding: '16px 0 6px',
    }}>
      {children}
    </div>
  );
}

function Card({ children }) {
  return (
    <div style={{
      background: t.colors.bgPanel, border: `1px solid ${t.colors.border}`,
      borderRadius: t.radii.lg, overflow: 'hidden', marginBottom: '6px',
    }}>
      {children}
    </div>
  );
}

function Row({ label, sub, children }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 14px', borderBottom: `1px solid ${t.colors.border}`,
    }}>
      <div>
        <div style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
          {label}
        </div>
        {sub && (
          <div style={{ fontSize: '11px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginTop: '2px' }}>
            {sub}
          </div>
        )}
      </div>
      {children}
    </div>
  );
}

function TextInput({ value, onSave, placeholder, type = 'text' }) {
  const [val, setVal] = useState(value || '');
  useEffect(() => setVal(value || ''), [value]);
  return (
    <div style={{ padding: '12px 14px' }}>
      <input
        type={type}
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={() => onSave(val)}
        placeholder={placeholder}
        style={{
          width: '100%', background: t.colors.bgInput,
          border: `1px solid ${t.colors.border}`, borderRadius: t.radii.md,
          color: t.colors.textPrimary, fontFamily: t.fonts.mono,
          fontSize: '13px', padding: '10px 12px', outline: 'none',
        }}
      />
    </div>
  );
}

function TimeRow({ label, startKey, endKey, config, onSave }) {
  const [start, setStart] = useState(config[startKey] || '');
  const [end,   setEnd]   = useState(config[endKey]   || '');

  const inputStyle = {
    background: t.colors.bgInput, border: `1px solid ${t.colors.border}`,
    borderRadius: t.radii.sm, color: t.colors.textPrimary,
    fontFamily: t.fonts.mono, fontSize: '13px', padding: '6px 8px',
    outline: 'none', width: '90px', colorScheme: 'dark',
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', borderBottom: `1px solid ${t.colors.border}` }}>
      <div style={{ fontSize: '13px', color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input type="time" value={start} onChange={e => setStart(e.target.value)}
          onBlur={() => onSave({ [startKey]: start })} style={inputStyle} />
        <span style={{ color: t.colors.textMuted, fontSize: '12px' }}>–</span>
        <input type="time" value={end} onChange={e => setEnd(e.target.value)}
          onBlur={() => onSave({ [endKey]: end })} style={inputStyle} />
      </div>
    </div>
  );
}

function TagInput({ value, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const tags = value || [];

  function addTag() {
    const trimmed = input.trim().toLowerCase();
    if (trimmed && !tags.includes(trimmed)) onChange([...tags, trimmed]);
    setInput('');
  }

  return (
    <div style={{ padding: '12px 14px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: tags.length ? '8px' : 0 }}>
        {tags.map(tag => (
          <div key={tag} style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            background: t.colors.bgInput, border: `1px solid ${t.colors.border}`,
            borderRadius: t.radii.pill, padding: '3px 10px',
            fontSize: '12px', color: t.colors.textPrimary, fontFamily: t.fonts.mono,
          }}>
            {tag}
            <button onClick={() => onChange(tags.filter(t => t !== tag))} style={{
              background: 'none', border: 'none', color: t.colors.textMuted,
              cursor: 'pointer', padding: '0 0 0 4px', fontSize: '14px', lineHeight: 1,
            }}>×</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          type="text" value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addTag()}
          placeholder={placeholder}
          style={{
            flex: 1, background: t.colors.bgInput, border: `1px solid ${t.colors.border}`,
            borderRadius: t.radii.md, color: t.colors.textPrimary,
            fontFamily: t.fonts.mono, fontSize: '13px', padding: '8px 10px', outline: 'none',
          }}
        />
        <button onClick={addTag} style={{
          padding: '8px 12px', background: t.colors.bgInput,
          border: `1px solid ${t.colors.border}`, borderRadius: t.radii.md,
          color: t.colors.textSecondary, fontSize: '12px', cursor: 'pointer', fontFamily: t.fonts.body,
        }}>Add</button>
      </div>
    </div>
  );
}

function OAuthCard({ service, connected, onReconnect }) {
  const label = service === 'gmail' ? 'Gmail' : 'Google Calendar';
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 14px', borderBottom: `1px solid ${t.colors.border}` }}>
      <div style={{ fontSize: '13px', fontWeight: 500,
        color: connected ? t.colors.success : t.colors.danger, fontFamily: t.fonts.body }}>
        {label} — {connected ? 'Connected' : 'Not connected'}
      </div>
      {!connected && (
        <button onClick={onReconnect} style={{
          padding: '6px 12px', background: t.colors.accent, border: 'none',
          borderRadius: t.radii.pill, color: '#fff', fontSize: '12px',
          fontWeight: 600, fontFamily: t.fonts.body, cursor: 'pointer',
        }}>
          Connect
        </button>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function TriageSettings({ userId }) {
  const [config,  setConfig]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [saved,   setSaved]   = useState(false);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!userId) return;
    getConfig(userId)
      .then(data => { setConfig(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [userId]);

  async function save(updates) {
    try {
      const updated = await updateConfig(userId, updates);
      setConfig(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err.message);
    }
  }

  function handleReconnect(service) {
    window.location.href = `${import.meta.env.VITE_API_URL}/api/oauth/${service}/connect?userId=${userId}`;
  }

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: t.colors.textMuted, fontFamily: t.fonts.body, fontSize: '14px' }}>
      Loading...
    </div>
  );

  if (!config) return null;

  const gmailOk = config.gmail_connected && !config.oauth_invalid;
  const calOk   = config.calendar_connected && !config.oauth_invalid;

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 80px' }}>

      {saved && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 10,
          padding: '10px 14px', background: t.colors.successDim,
          border: `1px solid ${t.colors.success}44`, borderRadius: t.radii.md,
          fontSize: '13px', color: t.colors.success, fontFamily: t.fonts.body,
          marginBottom: '8px', textAlign: 'center',
        }}>
          Settings saved
        </div>
      )}

      {error && (
        <div style={{
          padding: '10px 14px', background: t.colors.dangerDim,
          border: `1px solid ${t.colors.danger}44`, borderRadius: t.radii.md,
          fontSize: '13px', color: t.colors.danger, fontFamily: t.fonts.body, marginBottom: '8px',
        }}>
          {error}
        </div>
      )}

      {/* ── Active toggle ── */}
      <SectionLabel>Triage active</SectionLabel>
      <Card>
        <Row label="Run triage automatically" sub="Pause to stop scheduled runs">
          <button
            onClick={() => save({ triage_active: !config.triage_active })}
            style={{
              width: '44px', height: '24px', borderRadius: '12px',
              background: config.triage_active ? t.colors.accent : t.colors.bgInput,
              border: `1px solid ${config.triage_active ? t.colors.accent : t.colors.border}`,
              cursor: 'pointer', position: 'relative', transition: t.transitions.fast,
            }}
          >
            <div style={{
              position: 'absolute', top: '3px',
              left: config.triage_active ? '22px' : '3px',
              width: '16px', height: '16px', borderRadius: '50%',
              background: '#fff', transition: t.transitions.fast,
            }} />
          </button>
        </Row>
      </Card>

      {/* ── Gmail connections ── */}
      <SectionLabel>Gmail connections</SectionLabel>
      <Card>
        <OAuthCard service="gmail"    connected={gmailOk} onReconnect={() => handleReconnect('gmail')} />
        <OAuthCard service="calendar" connected={calOk}   onReconnect={() => handleReconnect('calendar')} />
      </Card>

      {/* ── Company info ── */}
      <SectionLabel>Company info</SectionLabel>
      <Card>
        <div style={{ borderBottom: `1px solid ${t.colors.border}` }}>
          <div style={{ padding: '10px 14px 4px', fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
            Company name (used in drafts)
          </div>
          <TextInput
            value={config.company_name}
            onSave={val => save({ company_name: val })}
            placeholder="Mint Roofing"
          />
        </div>
        <div>
          <div style={{ padding: '10px 14px 4px', fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
            Emergency phone (used in LEAK drafts)
          </div>
          <TextInput
            value={config.emergency_phone}
            onSave={val => save({ emergency_phone: val })}
            placeholder="952-473-8181"
            type="tel"
          />
        </div>
      </Card>

      {/* ── Draft mode ── */}
      <SectionLabel>Draft mode</SectionLabel>
      <Card>
        {[
          { val: 'auto',          label: 'Drafts + calendar blocks', sub: 'Agent writes drafts and adds calendar reminders' },
          { val: 'calendar_only', label: 'Calendar blocks only',     sub: 'Classifies and adds reminders, no drafts' },
          { val: 'off',           label: 'Classify only',            sub: 'Labels and sorts — no drafts or blocks' },
        ].map((opt, i) => (
          <button key={opt.val} onClick={() => save({ draft_mode: opt.val })} style={{
            display: 'flex', alignItems: 'center', width: '100%',
            padding: '12px 14px', background: 'none',
            border: 'none', borderTop: i === 0 ? 'none' : `1px solid ${t.colors.border}`,
            cursor: 'pointer', gap: '12px', textAlign: 'left',
          }}>
            <div style={{
              width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
              border: `2px solid ${config.draft_mode === opt.val ? t.colors.accent : t.colors.border}`,
              background: config.draft_mode === opt.val ? t.colors.accent : 'transparent',
              position: 'relative', transition: t.transitions.fast,
            }}>
              {config.draft_mode === opt.val && (
                <div style={{ position: 'absolute', top: '3px', left: '3px',
                  width: '8px', height: '8px', borderRadius: '50%', background: '#fff' }} />
              )}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
                {opt.label}
              </div>
              <div style={{ fontSize: '11px', color: t.colors.textSecondary, fontFamily: t.fonts.body, marginTop: '2px' }}>
                {opt.sub}
              </div>
            </div>
          </button>
        ))}
      </Card>

      {/* ── Scheduling windows ── */}
      <SectionLabel>Scheduling windows</SectionLabel>
      <Card>
        <TimeRow label="Morning"   startKey="eod_block_start_morning"   endKey="eod_block_end_morning"   config={config} onSave={save} />
        <TimeRow label="Midday"    startKey="eod_block_start_midday"    endKey="eod_block_end_midday"    config={config} onSave={save} />
        <TimeRow label="Afternoon" startKey="eod_block_start_afternoon" endKey="eod_block_end_afternoon" config={config} onSave={save} />
      </Card>

      {/* ── Key accounts (always EOD) ── */}
      <SectionLabel>Key accounts — always EOD</SectionLabel>
      <div style={{ padding: '0 0 4px', fontSize: '12px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
        Any email from these domains or senders is automatically classified EOD (e.g. cbre.com)
      </div>
      <Card>
        <div style={{ borderBottom: `1px solid ${t.colors.border}` }}>
          <div style={{ padding: '10px 14px 4px', fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>Domains</div>
          <TagInput
            value={config.eod_override_domains}
            onChange={val => save({ eod_override_domains: val })}
            placeholder="e.g. cbre.com (press Enter)"
          />
        </div>
        <div>
          <div style={{ padding: '10px 14px 4px', fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>Sender names</div>
          <TagInput
            value={config.eod_override_names}
            onChange={val => save({ eod_override_names: val })}
            placeholder="e.g. CBRE (press Enter)"
          />
        </div>
      </Card>

      {/* ── Always This Week overrides ── */}
      <SectionLabel>Cap at This Week — never escalate</SectionLabel>
      <div style={{ padding: '0 0 4px', fontSize: '12px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
        Emails from these senders are capped at This Week even if they contain urgent language (e.g. speedway.com)
      </div>
      <Card>
        <div style={{ borderBottom: `1px solid ${t.colors.border}` }}>
          <div style={{ padding: '10px 14px 4px', fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>Domains</div>
          <TagInput
            value={config.this_week_override_domains}
            onChange={val => save({ this_week_override_domains: val })}
            placeholder="e.g. speedway.com (press Enter)"
          />
        </div>
        <div>
          <div style={{ padding: '10px 14px 4px', fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>Sender names</div>
          <TagInput
            value={config.this_week_override_names}
            onChange={val => save({ this_week_override_names: val })}
            placeholder="e.g. Speedway (press Enter)"
          />
        </div>
      </Card>

      {/* ── Draft voice ── */}
      <SectionLabel>Draft voice</SectionLabel>
      <Card>
        <div style={{ padding: '12px 14px' }}>
          <textarea
            defaultValue={config.draft_voice_note || ''}
            onBlur={e => save({ draft_voice_note: e.target.value })}
            placeholder="e.g. Direct, confident, warm. Midwest straight-talk. 3-5 sentences max."
            rows={3}
            style={{
              width: '100%', background: t.colors.bgInput,
              border: `1px solid ${t.colors.border}`, borderRadius: t.radii.md,
              color: t.colors.textPrimary, fontFamily: t.fonts.body,
              fontSize: '13px', padding: '10px 12px', resize: 'none', outline: 'none', lineHeight: 1.5,
            }}
          />
          <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body, marginTop: '6px' }}>
            Used by Claude when writing your draft replies
          </div>
        </div>
      </Card>

      {/* ── Emergency contact email ── */}
      <SectionLabel>Emergency contact email</SectionLabel>
      <Card>
        <div style={{ padding: '12px 14px' }}>
          <input
            type="email"
            defaultValue={config.maintenance_email || ''}
            onBlur={e => save({ maintenance_email: e.target.value })}
            placeholder="leaks@yourcompany.com"
            style={{
              width: '100%', background: t.colors.bgInput,
              border: `1px solid ${t.colors.border}`, borderRadius: t.radii.md,
              color: t.colors.textPrimary, fontFamily: t.fonts.mono,
              fontSize: '13px', padding: '10px 12px', outline: 'none',
            }}
          />
          <div style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.body, marginTop: '6px' }}>
            Any email to this address automatically triggers LEAK tier
          </div>
        </div>
      </Card>

    </div>
  );
}
